import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Arrear, type BillingRun } from '../api';
import { date, money, nip, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Obciążenia i zaległości (E11-S6).
 *
 * Zadanie rozlicza i obciąża samo; ten ekran jest do sprawdzania, co się wydarzyło, i do dwóch
 * ręcznych ruchów: ponowienia obciążenia po rozmowie z klientem i przesunięcia trybu tylko do
 * odczytu — zawsze z uzasadnieniem, bo za miesiąc nikt nie będzie pamiętał, dlaczego klient
 * dostał dodatkowy tydzień.
 */

function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

export function Cycle({
  t,
  locale,
  onCompany,
  onOffice,
}: {
  t: Dictionary;
  locale: Locale;
  onCompany: (companyId: string) => void;
  onOffice: (officeId: string) => void;
}) {
  const [runs, setRuns] = useState<BillingRun[] | null>(null);
  const [arrears, setArrears] = useState<Arrear[] | null>(null);
  const [month, setMonth] = useState(lastMonth);
  const [postponing, setPostponing] = useState<{ id: string; to: string; note: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const [loadedRuns, loadedArrears] = await Promise.all([api.billingRuns(), api.arrears()]);
      setRuns(loadedRuns);
      setArrears(loadedArrears);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await action();
      setDone(true);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.cycle.title}</h3>
          <p>{t.cycle.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {done && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.cycle.done}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.cycle.arrears}</h4>
          </div>
        </div>
        {arrears === null ? (
          <p className="muted">{t.loading}</p>
        ) : arrears.length === 0 ? (
          <p className="muted">{t.cycle.noArrears}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.cycle.payer}</th>
                  <th>{t.cycle.period}</th>
                  <th className="n">{t.cycle.amount}</th>
                  <th className="wrap">{t.cycle.reason}</th>
                  <th className="n">{t.cycle.attempts}</th>
                  <th>{t.cycle.readOnlyFrom}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {arrears.map((arrear) => (
                  <tr key={arrear.id}>
                    <td className="wrap">
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => (arrear.companyId ? onCompany(arrear.companyId) : arrear.officeId && onOffice(arrear.officeId))}
                      >
                        <b>{arrear.payerName}</b>
                      </button>
                      <div className="hint">
                        {arrear.officeId ? `${t.cycle.office} · ` : ''}
                        <span className="mono">{nip(arrear.payerNip)}</span>
                      </div>
                    </td>
                    <td className="mono">{arrear.period}</td>
                    <td className="n mono">{money(arrear.amountGross, locale)}</td>
                    <td className="wrap">{t.cycle.reasons[arrear.reason] ?? arrear.reason}</td>
                    <td className="n mono">{arrear.attempts}</td>
                    <td>
                      {date(arrear.readOnlyFrom, locale)}
                      {arrear.readOnlyFrom <= today && <span className="chip st-bad">{t.cycle.readOnlyNow}</span>}
                    </td>
                    <td>
                      {postponing?.id === arrear.id ? (
                        <form
                          className="row"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void act(async () => {
                              await api.postponeArrear(arrear.id, postponing.to, postponing.note.trim());
                              setPostponing(null);
                            });
                          }}
                        >
                          <input
                            className="inp mono"
                            type="date"
                            aria-label={t.cycle.postponeTo}
                            value={postponing.to}
                            onChange={(event) => setPostponing({ ...postponing, to: event.target.value })}
                            required
                          />
                          <input
                            className="inp"
                            aria-label={t.cycle.note}
                            placeholder={t.cycle.note}
                            value={postponing.note}
                            onChange={(event) => setPostponing({ ...postponing, note: event.target.value })}
                            required
                          />
                          <button type="submit" className="btn sm" disabled={busy || postponing.note.trim() === ''}>
                            {t.cycle.save}
                          </button>
                        </form>
                      ) : (
                        <div className="row">
                          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void act(() => api.retryArrear(arrear.id))}>
                            {t.cycle.retry}
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => setPostponing({ id: arrear.id, to: arrear.readOnlyFrom, note: '' })}
                          >
                            {t.cycle.postpone}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid two">
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.cycle.runs}</h4>
            </div>
          </div>
          {runs === null ? (
            <p className="muted">{t.loading}</p>
          ) : runs.length === 0 ? (
            <p className="muted">{t.cycle.noRuns}</p>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.cycle.job}</th>
                    <th>{t.cycle.key}</th>
                    <th>{t.cycle.started}</th>
                    <th className="n">{t.cycle.settled}</th>
                    <th className="n">{t.cycle.charged}</th>
                    <th className="n">{t.cycle.failed}</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td>{t.cycle.jobs[run.job] ?? run.job}</td>
                      <td className="mono">{run.runKey}</td>
                      <td>
                        {date(run.startedAt, locale)}
                        {!run.finishedAt && <span className="chip st-warn">{t.cycle.running}</span>}
                      </td>
                      <td className="n mono">{run.settled}</td>
                      <td className="n mono">{run.charged}</td>
                      <td className="n mono">{run.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void act(() => api.runMonth(month));
          }}
        >
          <div className="hd">
            <div>
              <h4>{t.cycle.runMonth}</h4>
              <p>{t.cycle.runMonthLead}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.cycle.period}</span>
              <input
                className="inp mono"
                type="month"
                style={{ maxWidth: 150 }}
                value={month}
                max={lastMonth()}
                onChange={(event) => setMonth(event.target.value)}
                required
              />
            </label>
            <button type="submit" className="btn" disabled={busy}>
              {t.cycle.run}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
